import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Select,
  DatePicker,
  Row,
  Col,
  Typography,
  Alert,
  Popconfirm,
  Input,
  Collapse,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  StopOutlined,
  PrinterOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { usePaymentVouchers, usePostPaymentVoucher, useCancelPaymentVoucher } from '@/hooks/usePaymentVoucher';
import { useCurrency } from '@/contexts/CurrencyContext';
import type { PaymentVoucher } from '@/services/paymentVoucher.service';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Search } = Input;

const PaymentVouchersPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const { formatCurrency } = useCurrency();
  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([] as any);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [paymentTypeFilter, setPaymentTypeFilter] = useState<string>('');
  const [paymentModeFilter, setPaymentModeFilter] = useState<string>('');

  const paymentTypes = useMemo(
    () => [
      { value: 'SupplierBillPayment', label: t('paymentTypeSupplierBillPayment') },
      { value: 'SupplierAdvance', label: t('paymentTypeSupplierAdvance') },
      { value: 'EmployeePayment', label: t('paymentTypeEmployeePayment') },
      { value: 'ExpensePayment', label: t('paymentTypeExpensePayment') },
      { value: 'DirectPayment', label: t('paymentTypeDirectPayment') },
      { value: 'BankTransfer', label: t('paymentTypeBankTransfer') },
      { value: 'CashTransfer', label: t('paymentTypeCashTransfer') },
      { value: 'AccountTransfer', label: t('paymentTypeAccountTransfer') },
    ],
    [t]
  );

  const paymentModes = useMemo(
    () => [
      { value: 'Cash', label: t('modeCash') },
      { value: 'Cheque', label: t('modeCheque') },
      { value: 'BankTransfer', label: t('modeBankTransfer') },
      { value: 'Online', label: t('modeOnline') },
      { value: 'Card', label: t('modeCard') },
      { value: 'PDC', label: t('modePdc') },
      { value: 'Other', label: t('modeOther') },
    ],
    [t]
  );

  const paymentTypeLabel = (value?: string) =>
    paymentTypes.find((p) => p.value === value)?.label ?? value ?? '—';

  const paymentModeLabel = (value?: string) =>
    paymentModes.find((p) => p.value === value)?.label ?? value ?? '—';

  const statusLabel = (status: string) => {
    const map: Record<string, string> = {
      Draft: t('statusDraft'),
      Submitted: tc('submitted'),
      Approved: t('statusApproved'),
      Posted: t('statusPosted'),
      Cancelled: t('statusCancelled'),
    };
    return map[status] ?? status;
  };

  const filters = useMemo(
    () => ({
      search: searchText.trim() || undefined,
      from_date: dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : undefined,
      to_date: dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
      status: statusFilter || undefined,
      payment_type: paymentTypeFilter || undefined,
      payment_mode: paymentModeFilter || undefined,
    }),
    [searchText, dateRange, statusFilter, paymentTypeFilter, paymentModeFilter]
  );
  const { data: list = [], isLoading } = usePaymentVouchers(filters);
  const postMutation = usePostPaymentVoucher();
  const cancelMutation = useCancelPaymentVoucher();

  const applySearch = (value?: string) => {
    setSearchText((value ?? searchInput).trim());
  };

  const clearAdvanced = () => {
    setDateRange([] as any);
    setStatusFilter('');
    setPaymentTypeFilter('');
    setPaymentModeFilter('');
  };

  const columns: ColumnsType<PaymentVoucher> = [
    {
      title: t('paymentVoucherNo'),
      dataIndex: 'voucher_number',
      key: 'voucher_number',
      fixed: 'left',
      width: 130,
      render: (val: string, record: PaymentVoucher) => (
        <a onClick={() => navigate(`/fin/payment-vouchers/${record.id}`)}>{val ?? '—'}</a>
      ),
    },
    {
      title: t('date'),
      dataIndex: 'voucher_date',
      key: 'voucher_date',
      width: 110,
      render: (d: string) => (d ? dayjs(d).format('DD MMM YYYY') : '-'),
    },
    {
      title: t('type'),
      dataIndex: 'payment_type',
      key: 'payment_type',
      width: 150,
      render: (v: string) => paymentTypeLabel(v),
    },
    {
      title: t('payee'),
      key: 'payee',
      width: 180,
      render: (_, r) => {
        if (r.settlement_scope === 'MultiSupplier' || (!r.supplier_id && r.payment_type === 'SupplierBillPayment')) {
          return r.payee_name || t('multiSupplier');
        }
        return (r as any).supplier?.supplier_name ?? r.payee_name ?? '-';
      },
    },
    {
      title: t('amount'),
      dataIndex: 'amount',
      key: 'amount',
      width: 130,
      align: 'right',
      render: (v: number, r: PaymentVoucher) => {
        const seen = new Set<string>();
        const charges = (r.chargeLines ?? []).reduce((sum, line) => {
          if (line.id) {
            if (seen.has(line.id)) return sum;
            seen.add(line.id);
          }
          return sum + Number(line.amount || 0);
        }, 0);
        return formatCurrency(Number(v) + charges, r.currency);
      },
    },
    {
      title: t('allocations'),
      key: 'allocations',
      width: 90,
      align: 'center',
      render: (_: unknown, r: PaymentVoucher) =>
        r.payment_type === 'SupplierBillPayment' ? (r.allocations?.length ?? 0) : '—',
    },
    {
      title: t('mode'),
      dataIndex: 'payment_mode',
      key: 'payment_mode',
      width: 100,
      render: (v: string) => paymentModeLabel(v),
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (s: string) => {
        const color = s === 'Posted' ? 'green' : s === 'Cancelled' ? 'red' : 'default';
        return <Tag color={color}>{statusLabel(s)}</Tag>;
      },
    },
    {
      title: t('actions'),
      key: 'actions',
      fixed: 'right',
      width: 280,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/fin/payment-vouchers/${record.id}`)}
          >
            {tc('view')}
          </Button>
          {record.status === 'Draft' && (
            <>
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => navigate(`/fin/payment-vouchers/${record.id}/edit`)}
              >
                {tc('edit')}
              </Button>
              <Button
                type="link"
                size="small"
                icon={<CheckCircleOutlined />}
                onClick={() => postMutation.mutate(record.id)}
                loading={postMutation.isPending}
              >
                {tc('post')}
              </Button>
            </>
          )}
          {record.status === 'Posted' && (
            <>
              <Button
                type="link"
                size="small"
                icon={<PrinterOutlined />}
                onClick={() => navigate(`/fin/payment-vouchers/${record.id}?print=receipt`)}
              >
                {t('printReceipt')}
              </Button>
              <Button
                type="link"
                size="small"
                icon={<PrinterOutlined />}
                onClick={() => navigate(`/fin/payment-vouchers/${record.id}?print=voucher`)}
              >
                {t('printVoucher')}
              </Button>
            </>
          )}
          {record.status !== 'Cancelled' &&
            ['Draft', 'Submitted', 'Approved', 'Posted'].includes(record.status) && (
              <Popconfirm
                title={t('cancelThisPaymentVoucher')}
                description={
                  record.status === 'Posted' ? t('cancelPvPostedDesc') : t('cancelPvDraftDesc')
                }
                onConfirm={() => cancelMutation.mutate(record.id)}
                okText={t('cancelVoucher')}
                cancelText={tc('keep')}
              >
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={<StopOutlined />}
                  loading={cancelMutation.isPending}
                >
                  {tc('cancel')}
                </Button>
              </Popconfirm>
            )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            {t('paymentVouchers')}
          </Title>
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 16, maxWidth: 720 }}
            message={t('paymentVoucherRegister')}
            description={t('searchPaymentHint')}
          />
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/fin/payment-vouchers/new')}>
            {t('newPaymentVoucher')}
          </Button>
        </Col>
      </Row>
      <Card>
        <Space direction="vertical" size="middle" style={{ width: '100%', marginBottom: 16 }}>
          <Search
            placeholder={t('searchPaymentPlaceholder')}
            allowClear
            enterButton={<SearchOutlined />}
            value={searchInput}
            onChange={(e) => {
              const v = e.target.value;
              setSearchInput(v);
              if (!v.trim()) setSearchText('');
            }}
            onSearch={applySearch}
            style={{ maxWidth: 720 }}
          />
          <Collapse
            ghost
            items={[
              {
                key: 'advanced',
                label: <Text type="secondary">{t('advancedFilters')}</Text>,
                children: (
                  <Space wrap>
                    <RangePicker value={dateRange} onChange={(v) => setDateRange(v as any)} />
                    <Select
                      placeholder={t('status')}
                      allowClear
                      style={{ width: 120 }}
                      value={statusFilter || undefined}
                      onChange={(v) => setStatusFilter(v ?? '')}
                      options={[
                        { value: 'Draft', label: t('statusDraft') },
                        { value: 'Posted', label: t('statusPosted') },
                        { value: 'Cancelled', label: t('statusCancelled') },
                      ]}
                    />
                    <Select
                      placeholder={t('paymentType')}
                      allowClear
                      style={{ width: 180 }}
                      value={paymentTypeFilter || undefined}
                      onChange={(v) => setPaymentTypeFilter(v ?? '')}
                      options={paymentTypes}
                    />
                    <Select
                      placeholder={t('paymentMode')}
                      allowClear
                      style={{ width: 140 }}
                      value={paymentModeFilter || undefined}
                      onChange={(v) => setPaymentModeFilter(v ?? '')}
                      options={paymentModes}
                    />
                    <Button onClick={clearAdvanced}>{t('clearFilters')}</Button>
                  </Space>
                ),
              },
            ]}
          />
        </Space>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={list}
          loading={isLoading}
          sticky={{ offsetHeader: 0 }}
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => t('totalVouchers', { count: total }),
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          scroll={{ x: 1200, y: 520 }}
          size="small"
        />
      </Card>
    </div>
  );
};

export default PaymentVouchersPage;
